/**
 * Zustand store for multi-channel Marketing integration state.
 */

import { create } from 'zustand'
import type {
  MarketingChannel,
  AdAccount,
  MetaAdAccount,
  Campaign,
  AdSet,
  Ad,
  ConversionEvent,
  AnalyticsOverview,
  CampaignPerformanceRow,
  AttributionFunnelStep,
  TimeSeriesPoint,
  Audience,
  OptimizationAction,
  Creative,
} from '@/types/marketing'

interface MarketingState {
  // Channel filter (null = all channels)
  selectedChannel: MarketingChannel | null
  // Shared analytics date range (days) — one range across the marketing
  // surfaces (dashboard toolbar + Analytics tab) instead of per-tab state.
  dateRangeDays: number
  // True once the user explicitly picked a range this session; seeding from a
  // dashboard's persisted default must not clobber an explicit choice.
  dateRangeTouched: boolean
  connectedChannels: MarketingChannel[]
  adAccounts: AdAccount[]

  // Account (legacy Meta)
  account: MetaAdAccount | null
  hasAccount: boolean

  // Campaigns
  campaigns: Campaign[]
  // Cross-widget campaign selection (multi-select). Composes AND with
  // selectedChannel + dateRangeDays on the analytics fetches (spec §4.4).
  selectedCampaignIds: string[]
  adSets: AdSet[]
  ads: Ad[]

  // Analytics
  overview: AnalyticsOverview | null
  campaignPerformance: CampaignPerformanceRow[]
  attributionFunnel: AttributionFunnelStep[]
  /** Completed in-window orders with no resolvable campaign attribution —
   * only reported on the unfiltered funnel view (null otherwise). */
  attributionUnattributed: number | null
  timeSeries: TimeSeriesPoint[]

  // Conversions
  conversionEvents: ConversionEvent[]
  conversionTotal: number

  // Audiences
  audiences: Audience[]

  // Optimization
  optimizationActions: OptimizationAction[]

  // Creatives
  creatives: Creative[]

  // Loading state
  isLoading: boolean

  // Setters
  setSelectedChannel: (channel: MarketingChannel | null) => void
  setDateRangeDays: (days: number) => void
  seedDateRangeDays: (days: number) => void
  setConnectedChannels: (channels: MarketingChannel[]) => void
  setAdAccounts: (accounts: AdAccount[]) => void
  setAccount: (account: MetaAdAccount | null) => void
  setHasAccount: (has: boolean) => void
  setCampaigns: (campaigns: Campaign[]) => void
  toggleCampaignId: (id: string) => void
  setSelectedCampaignIds: (ids: string[]) => void
  clearSelectedCampaigns: () => void
  setAdSets: (adSets: AdSet[]) => void
  setAds: (ads: Ad[]) => void
  setOverview: (overview: AnalyticsOverview | null) => void
  setCampaignPerformance: (rows: CampaignPerformanceRow[]) => void
  setAttributionFunnel: (steps: AttributionFunnelStep[]) => void
  setAttributionUnattributed: (count: number | null) => void
  setTimeSeries: (points: TimeSeriesPoint[]) => void
  setConversionEvents: (events: ConversionEvent[], total: number) => void
  setAudiences: (audiences: Audience[]) => void
  setOptimizationActions: (actions: OptimizationAction[]) => void
  setCreatives: (creatives: Creative[]) => void
  setLoading: (loading: boolean) => void
  reset: () => void
}

const initialState = {
  selectedChannel: null as MarketingChannel | null,
  dateRangeDays: 30,
  dateRangeTouched: false,
  connectedChannels: [] as MarketingChannel[],
  adAccounts: [] as AdAccount[],
  account: null,
  hasAccount: false,
  campaigns: [],
  selectedCampaignIds: [] as string[],
  adSets: [],
  ads: [],
  overview: null,
  campaignPerformance: [],
  attributionFunnel: [],
  attributionUnattributed: null,
  timeSeries: [],
  conversionEvents: [],
  conversionTotal: 0,
  audiences: [],
  optimizationActions: [],
  creatives: [],
  isLoading: false,
}

export const useMarketingStore = create<MarketingState>()((set) => ({
  ...initialState,

  setSelectedChannel: (channel) => set({ selectedChannel: channel }),
  setDateRangeDays: (days) => set({ dateRangeDays: days, dateRangeTouched: true }),
  seedDateRangeDays: (days) =>
    set((s) => (s.dateRangeTouched ? {} : { dateRangeDays: days })),
  setConnectedChannels: (channels) => set({ connectedChannels: channels }),
  setAdAccounts: (accounts) => set({ adAccounts: accounts }),
  setAccount: (account) => set({ account }),
  setHasAccount: (has) => set({ hasAccount: has }),
  setCampaigns: (campaigns) => set({ campaigns }),
  toggleCampaignId: (id) =>
    set((s) => ({
      selectedCampaignIds: s.selectedCampaignIds.includes(id)
        ? s.selectedCampaignIds.filter((c) => c !== id)
        : [...s.selectedCampaignIds, id],
    })),
  setSelectedCampaignIds: (ids) => set({ selectedCampaignIds: ids }),
  clearSelectedCampaigns: () => set({ selectedCampaignIds: [] }),
  setAdSets: (adSets) => set({ adSets }),
  setAds: (ads) => set({ ads }),
  setOverview: (overview) => set({ overview }),
  setCampaignPerformance: (rows) => set({ campaignPerformance: rows }),
  setAttributionFunnel: (steps) => set({ attributionFunnel: steps }),
  setAttributionUnattributed: (count) => set({ attributionUnattributed: count }),
  setTimeSeries: (points) => set({ timeSeries: points }),
  setConversionEvents: (events, total) => set({ conversionEvents: events, conversionTotal: total }),
  setAudiences: (audiences) => set({ audiences }),
  setOptimizationActions: (actions) => set({ optimizationActions: actions }),
  setCreatives: (creatives) => set({ creatives }),
  setLoading: (loading) => set({ isLoading: loading }),
  reset: () => set(initialState),
}))
